import { ReactNode } from 'react';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import ScheduleIcon from '@material-ui/icons/Schedule';
import DescriptionIcon from '@material-ui/icons/Description';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { NotificationType } from './types';

export const TYPE_TAB: Record<
  NotificationType,
  'approvals' | 'vmUpdates' | 'messages' | 'other'
> = {
  APPROVAL_REQUIRED: 'approvals',
  VM_REQUEST_APPROVED: 'vmUpdates',
  VM_REQUEST_REJECTED: 'vmUpdates',
  EXTENSION_REQUEST_APPROVED: 'vmUpdates',
  EXTENSION_REQUEST_REJECTED: 'vmUpdates',
  ACCESS_REQUESTED: 'approvals',
  ACCESS_APPROVED: 'vmUpdates',
  ACCESS_REJECTED: 'vmUpdates',
  VM_ACCESS_GRANTED: 'vmUpdates',
  VM_PROVISIONING_COMPLETED: 'vmUpdates',
  VM_PROVISIONING_FAILED: 'vmUpdates',
  VM_SHUTDOWN_WARNING: 'vmUpdates',
  VM_STOPPED: 'vmUpdates',
  REQUEST_UPDATED: 'vmUpdates',
  NEW_MESSAGE: 'messages',
  REQUEST_COMMENT_ADDED: 'messages',
  CHANGE_REQUESTED: 'approvals',
  CHANGE_APPROVED: 'vmUpdates',
  CHANGE_REJECTED: 'vmUpdates',
  CHANGE_COMPLETED: 'vmUpdates',
  CHANGE_FAILED: 'vmUpdates',
  JUSTIFICATION_REQUESTED: 'approvals',
  JUSTIFICATION_SUBMITTED: 'approvals',
  JUSTIFICATION_REVIEWED: 'vmUpdates',
  GOVERNANCE_NOTICE: 'other',
};

const ICON_BY_TYPE: Record<
  NotificationType,
  { icon: ReactNode; color: 'ok' | 'error' | 'warning' | 'default' }
> = {
  VM_REQUEST_APPROVED: {
    icon: <CheckCircleIcon fontSize="small" />,
    color: 'ok',
  },
  EXTENSION_REQUEST_APPROVED: {
    icon: <CheckCircleIcon fontSize="small" />,
    color: 'ok',
  },
  VM_PROVISIONING_COMPLETED: {
    icon: <CheckCircleIcon fontSize="small" />,
    color: 'ok',
  },
  ACCESS_APPROVED: {
    icon: <CheckCircleIcon fontSize="small" />,
    color: 'ok',
  },
  VM_ACCESS_GRANTED: {
    icon: <CheckCircleIcon fontSize="small" />,
    color: 'ok',
  },
  ACCESS_REJECTED: { icon: <CancelIcon fontSize="small" />, color: 'error' },
  ACCESS_REQUESTED: {
    icon: <ScheduleIcon fontSize="small" />,
    color: 'warning',
  },
  VM_REQUEST_REJECTED: {
    icon: <CancelIcon fontSize="small" />,
    color: 'error',
  },
  EXTENSION_REQUEST_REJECTED: {
    icon: <CancelIcon fontSize="small" />,
    color: 'error',
  },
  VM_PROVISIONING_FAILED: {
    icon: <CancelIcon fontSize="small" />,
    color: 'error',
  },
  VM_STOPPED: { icon: <CancelIcon fontSize="small" />, color: 'error' },
  NEW_MESSAGE: {
    icon: <ChatBubbleOutlineIcon fontSize="small" />,
    color: 'default',
  },
  REQUEST_COMMENT_ADDED: {
    icon: <ChatBubbleOutlineIcon fontSize="small" />,
    color: 'default',
  },
  APPROVAL_REQUIRED: {
    icon: <ScheduleIcon fontSize="small" />,
    color: 'warning',
  },
  VM_SHUTDOWN_WARNING: {
    icon: <ScheduleIcon fontSize="small" />,
    color: 'warning',
  },
  REQUEST_UPDATED: {
    icon: <DescriptionIcon fontSize="small" />,
    color: 'default',
  },
  CHANGE_REQUESTED: {
    icon: <ScheduleIcon fontSize="small" />,
    color: 'warning',
  },
  CHANGE_APPROVED: {
    icon: <CheckCircleIcon fontSize="small" />,
    color: 'ok',
  },
  CHANGE_COMPLETED: {
    icon: <CheckCircleIcon fontSize="small" />,
    color: 'ok',
  },
  CHANGE_REJECTED: { icon: <CancelIcon fontSize="small" />, color: 'error' },
  CHANGE_FAILED: { icon: <CancelIcon fontSize="small" />, color: 'error' },
  JUSTIFICATION_REQUESTED: {
    icon: <ScheduleIcon fontSize="small" />,
    color: 'warning',
  },
  JUSTIFICATION_SUBMITTED: {
    icon: <DescriptionIcon fontSize="small" />,
    color: 'default',
  },
  JUSTIFICATION_REVIEWED: {
    icon: <CheckCircleIcon fontSize="small" />,
    color: 'ok',
  },
  GOVERNANCE_NOTICE: {
    icon: <NotificationsIcon fontSize="small" />,
    color: 'warning',
  },
};

export function iconForType(type: NotificationType) {
  return (
    ICON_BY_TYPE[type] ?? {
      icon: <NotificationsIcon fontSize="small" />,
      color: 'default' as const,
    }
  );
}

// The notifications-center table's "Type" column — a coarser, human label
// than the raw NotificationType, grouped by what actually happened rather
// than which specific flow it came from (an approved VM request and an
// approved extension both just read "Approval").
export type TypeChipTone = 'ok' | 'error' | 'warning' | 'info' | 'default';

const TYPE_CHIP: Record<
  NotificationType,
  { label: string; tone: TypeChipTone }
> = {
  VM_REQUEST_APPROVED: { label: 'Approval', tone: 'ok' },
  EXTENSION_REQUEST_APPROVED: { label: 'Approval', tone: 'ok' },
  VM_PROVISIONING_COMPLETED: { label: 'Update', tone: 'ok' },
  VM_REQUEST_REJECTED: { label: 'Rejection', tone: 'error' },
  EXTENSION_REQUEST_REJECTED: { label: 'Rejection', tone: 'error' },
  ACCESS_REQUESTED: { label: 'Approval', tone: 'warning' },
  ACCESS_APPROVED: { label: 'Approval', tone: 'ok' },
  ACCESS_REJECTED: { label: 'Rejection', tone: 'error' },
  VM_ACCESS_GRANTED: { label: 'Update', tone: 'ok' },
  VM_PROVISIONING_FAILED: { label: 'Rejection', tone: 'error' },
  VM_STOPPED: { label: 'Update', tone: 'error' },
  NEW_MESSAGE: { label: 'Message', tone: 'info' },
  REQUEST_COMMENT_ADDED: { label: 'Message', tone: 'info' },
  APPROVAL_REQUIRED: { label: 'Approval', tone: 'warning' },
  VM_SHUTDOWN_WARNING: { label: 'Update', tone: 'warning' },
  REQUEST_UPDATED: { label: 'Update', tone: 'default' },
  CHANGE_REQUESTED: { label: 'Approval', tone: 'warning' },
  CHANGE_APPROVED: { label: 'Approval', tone: 'ok' },
  CHANGE_COMPLETED: { label: 'Update', tone: 'ok' },
  CHANGE_REJECTED: { label: 'Rejection', tone: 'error' },
  CHANGE_FAILED: { label: 'Update', tone: 'error' },
  JUSTIFICATION_REQUESTED: { label: 'Action needed', tone: 'warning' },
  JUSTIFICATION_SUBMITTED: { label: 'Update', tone: 'info' },
  JUSTIFICATION_REVIEWED: { label: 'Update', tone: 'default' },
  GOVERNANCE_NOTICE: { label: 'Notice', tone: 'warning' },
};

export function typeChipFor(type: NotificationType) {
  return TYPE_CHIP[type] ?? { label: type, tone: 'default' as const };
}

export const RESOURCE_TYPE_LABEL: Record<string, string> = {
  VM_REQUEST: 'VM Request',
  EXTENSION_REQUEST: 'Extension Request',
  VM_ACCESS_REQUEST: 'Access Request',
  VM_INSTANCE: 'Virtual Machine',
  VM_CHANGE_REQUEST: 'Change Request',
  VM_JUSTIFICATION: 'Justification',
  VM_GOVERNANCE: 'VM Governance',
};

// Resolves a notification's (resourceType, resourceId) to an actual
// existing route in this app — never a made-up path. Only the two
// resource types this app's notification integrations currently produce
// (see the plan/report) have a real destination; anything else has no
// link, which the caller should render as plain (non-clickable) text.
export function resourceRoute(
  resourceType: string,
  resourceId: string,
): string | undefined {
  switch (resourceType) {
    case 'VM_REQUEST':
      return `/infrastructure/requests/${encodeURIComponent(resourceId)}`;
    case 'EXTENSION_REQUEST':
      return `/keep-vm-running/requests/${encodeURIComponent(resourceId)}`;
    case 'VM_ACCESS_REQUEST':
      return `/infrastructure/access-requests/${encodeURIComponent(
        resourceId,
      )}`;
    case 'VM_CHANGE_REQUEST':
      return `/infrastructure/change-requests/${encodeURIComponent(
        resourceId,
      )}`;
    case 'VM_GOVERNANCE':
      return '/infrastructure/my-vms';
    case 'VM_JUSTIFICATION':
      return `/infrastructure/justifications/${encodeURIComponent(resourceId)}`;
    case 'VM_INSTANCE':
      return `/infrastructure/instances/${encodeURIComponent(resourceId)}`;
    default:
      return undefined;
  }
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// A small fixed set of relative buckets, not a full "time ago" library —
// this app has no existing relative-time convention to match, and
// everywhere else already just calls toLocaleString() directly.
export function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < MINUTE_MS) return 'just now';
  if (diffMs < HOUR_MS) return `${Math.floor(diffMs / MINUTE_MS)}m ago`;
  if (diffMs < DAY_MS) return `${Math.floor(diffMs / HOUR_MS)}h ago`;
  if (diffMs < 7 * DAY_MS) return `${Math.floor(diffMs / DAY_MS)}d ago`;
  return new Date(iso).toLocaleDateString();
}
